/* ==========================================================================
   SLIDESHOW VIDEO AUDIO + VIDEO CONTROL
------------------------------------------------------------------------------
FEATURES:
- Shopify videos
- YouTube videos
- Prevents slick autoplay during video playback
- Advances slide ONLY after video ends
- Image slides still autoplay normally
- Arrow navigation still works
- Mutes all inactive videos
- Handles desktop/mobile media
- Prevents accidental restart on tiny swipe
- Allows swipe on YouTube slides
============================================================================= */

document.addEventListener('DOMContentLoaded', () => {

  const slider = window.jQuery
    ? jQuery('.home-slideshow__slides')
    : null;

  const audioButton = document.querySelector('[data-video-toggle]');

  if (!slider || !slider.length) return;

  /* ==========================================================================
     DEVICE DETECTION
  ========================================================================== */

  const isDesktop = () => {
    return window.innerWidth >= 769;
  };

  /* ==========================================================================
     TRACK REAL SLIDE CHANGES
  ========================================================================== */

  let previousSlideIndex = 0;

  /* ==========================================================================
     GET ACTIVE SLIDE
  ========================================================================== */

  const getActiveSlide = () => {

    return (
      document.querySelector('.home-slideshow__slide.slick-current') ||
      document.querySelector('.home-slideshow__slide')
    );

  };

  /* ==========================================================================
     GET CURRENT MEDIA
  ========================================================================== */

  const getCurrentMedia = () => {

    const activeSlide = getActiveSlide();

    if (!activeSlide) return {};

    /*
    --------------------------------------------------------------------------
    REAL VISIBILITY CHECK
    --------------------------------------------------------------------------
    */

    const visibleVideos = Array.from(
        activeSlide.querySelectorAll(
        '.slideshow-video, .slideshow-iframe'
        )
    ).filter(el => {

        return (
        el.offsetParent !== null &&
        window.getComputedStyle(el).display !== 'none' &&
        window.getComputedStyle(el).visibility !== 'hidden'
        );

    });

    let shopifyVideo = null;
    let youtubeIframe = null;

    visibleVideos.forEach(el => {

        if (el.classList.contains('slideshow-video')) {
        shopifyVideo = el;
        }

        if (el.classList.contains('slideshow-iframe')) {
        youtubeIframe = el;
        }

    });

    return {
        slide: activeSlide,
        shopifyVideo,
        youtubeIframe
    };

    };

  /* ==========================================================================
     YOUTUBE API
  ========================================================================== */

  let youtubePlayers = [];

  const getYouTubePlayer = (iframe) => {

    if (!iframe) return null;

    const item = youtubePlayers.find((p) => {

      return p.iframe === iframe;

    });

    return item ? item.player : null;

  };

  function loadYouTubeAPI() {

    return new Promise(resolve => {

      if (window.YT && window.YT.Player) {
        resolve();
        return;
      }

      const existing = document.querySelector(
        'script[src="https://www.youtube.com/iframe_api"]'
      );

      if (!existing) {

        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';

        document.head.appendChild(tag);

      }

      window.onYouTubeIframeAPIReady = () => {
        resolve();
      };

    });

  }

  /* ==========================================================================
     AUDIO STATE
  ========================================================================== */

  let isMuted = true;

  /* ==========================================================================
     UPDATE BUTTON
  ========================================================================== */

  const updateButtonState = () => {

    if (!audioButton) return;

    if (isMuted) {

      audioButton.classList.remove('is-unmuted');
      audioButton.classList.add('is-muted');

      audioButton.setAttribute('aria-label', 'Unmute video');

    } else {

      audioButton.classList.remove('is-muted');
      audioButton.classList.add('is-unmuted');

      audioButton.setAttribute('aria-label', 'Mute video');

    }

  };

  /* ==========================================================================
     MUTE ALL SHOPIFY VIDEOS
  ========================================================================== */

  const muteAllShopifyVideos = () => {

    document.querySelectorAll('.slideshow-video').forEach(video => {

      video.muted = true;

      const activeSlide = getActiveSlide();

      /* ==========================================================================
         DO NOT TOUCH ACTIVE VIDEO
      ========================================================================== */

      if (
        activeSlide &&
        activeSlide.contains(video)
      ) {
        return;
      }

      try {
        video.pause();
      } catch(e) {}

    });

  };

  /* ==========================================================================
     MUTE ALL YOUTUBE VIDEOS
  ========================================================================== */

  const muteAllYouTubeVideos = () => {

    youtubePlayers.forEach(item => {

      try {

        const player = item.player;

        player.mute();

        const iframe = item.iframe;

        const activeSlide = getActiveSlide();

        /* ==========================================================================
           DO NOT TOUCH ACTIVE VIDEO
        ========================================================================== */

        if (
          activeSlide &&
          activeSlide.contains(iframe)
        ) {
          return;
        }

        player.pauseVideo();

      } catch(e) {}

    });

  };

  /* ==========================================================================
     MUTE EVERYTHING
  ========================================================================== */

  const muteAllVideos = () => {

    muteAllShopifyVideos();
    muteAllYouTubeVideos();

  };

  /* ==========================================================================
     STOP SLICK AUTOPLAY
  ========================================================================== */

  const stopSliderAutoplay = () => {

    if (!slider.hasClass('slick-initialized')) return;

    slider.slick('slickPause');

  };

  /* ==========================================================================
     START SLICK AUTOPLAY
  ========================================================================== */

  const startSliderAutoplay = () => {

    if (!slider.hasClass('slick-initialized')) return;

    slider.slick('slickPlay');

  };

  /* ==========================================================================
     NEXT SLIDE
  ========================================================================== */

  const goToNextSlide = () => {

    muteAllVideos();

    isMuted = true;

    updateButtonState();

    slider.slick('slickNext');

  };

  /* ==========================================================================
     HANDLE ACTIVE SLIDE
  ========================================================================== */

  const handleActiveSlide = (forceRestart = false) => {

    muteAllVideos();

    const media = getCurrentMedia();

    /* ==========================================================================
       IMAGE SLIDE
    ========================================================================== */

    if (!media.shopifyVideo && !media.youtubeIframe) {

      startSliderAutoplay();

      return;

    }

    /* ==========================================================================
       VIDEO SLIDE
    ========================================================================== */

    stopSliderAutoplay();

    /* ==========================================================================
       SHOPIFY VIDEO
    ========================================================================== */

    if (media.shopifyVideo) {

      const video = media.shopifyVideo;

      video.muted = isMuted;

      /* ==========================================================================
         ONLY RESTART AFTER REAL SLIDE CHANGE
      ========================================================================== */

      if (forceRestart) {
        video.currentTime = 0;
      }

      video.play().catch(() => {});

      return;

    }

    /* ==========================================================================
       YOUTUBE VIDEO
    ========================================================================== */

    if (media.youtubeIframe) {

      const player = getYouTubePlayer(media.youtubeIframe);

      if (!player) return;

      try {

        if (forceRestart) {
          player.seekTo(0);
        }

        if (isMuted) {
          player.mute();
        } else {
          player.unMute();
        }

        player.playVideo();

      } catch(e) {}

    }

  };

  /* ==========================================================================
     SHOPIFY VIDEO EVENTS
  ========================================================================== */

  const initShopifyEvents = () => {

    document.querySelectorAll('.slideshow-video').forEach(video => {

      video.onended = null;

      video.addEventListener('ended', () => {

        const activeSlide = getActiveSlide();

        if (
          activeSlide &&
          activeSlide.contains(video)
        ) {

          goToNextSlide();

        }

      });

    });

  };

  /* ==========================================================================
     INIT YOUTUBE PLAYERS
  ========================================================================== */

  const initYouTubePlayers = () => {

    youtubePlayers = [];

    document.querySelectorAll('.slideshow-iframe').forEach(iframe => {

      /* ==========================================================================
         ALLOW SLICK SWIPE THROUGH YOUTUBE
      ========================================================================== */

      iframe.style.pointerEvents = 'none';

      const player = new YT.Player(iframe, {

        events: {

          onStateChange: (event) => {

            /* ==========================================================================
               VIDEO ENDED
            ========================================================================== */

            if (event.data === YT.PlayerState.ENDED) {

              const activeSlide = getActiveSlide();

              if (
                activeSlide &&
                activeSlide.contains(iframe)
              ) {

                goToNextSlide();

              }

            }

          }

        }

      });

      youtubePlayers.push({
        iframe,
        player
      });

    });

  };

  /* ==========================================================================
     AUDIO TOGGLE
  ========================================================================== */

  if (audioButton) {

    audioButton.addEventListener('click', () => {

      isMuted = !isMuted;

      const media = getCurrentMedia();

      /* ==========================================================================
         SHOPIFY VIDEO
      ========================================================================== */

      if (media.shopifyVideo) {

        media.shopifyVideo.muted = isMuted;

      }

      /* ==========================================================================
         YOUTUBE VIDEO
      ========================================================================== */

      if (media.youtubeIframe) {

        const player = getYouTubePlayer(media.youtubeIframe);

        if (player) {

        try {

            /*
            --------------------------------------------------------------------------
            FORCE PLAYBACK FIRST
            --------------------------------------------------------------------------
            */

            player.playVideo();

            if (isMuted) {

            player.mute();

            } else {

            /*
            ------------------------------------------------------------------------
            MOBILE SAFARI / CHROME FIX
            ------------------------------------------------------------------------
            */

            player.unMute();

            /*
            ------------------------------------------------------------------------
            FORCE VOLUME AFTER UNMUTE
            ------------------------------------------------------------------------
            */

            player.setVolume(100);

            /*
            ------------------------------------------------------------------------
            PLAY AGAIN AFTER UNMUTE
            ------------------------------------------------------------------------
            */

            setTimeout(() => {

                try {

                player.playVideo();

                } catch(e) {}

            }, 50);

            }

        } catch(e) {}

        }

      }

      updateButtonState();

    });

  }

  /* ==========================================================================
     BEFORE CHANGE
  ========================================================================== */

  slider.on('beforeChange', (event, slick, currentSlide, nextSlide) => {

    /* ==========================================================================
       IGNORE FALSE TOUCH RE-TRIGGERS
    ========================================================================== */

    if (currentSlide === nextSlide) {
      return;
    }

  });

  /* ==========================================================================
     AFTER CHANGE
  ========================================================================== */

  slider.on('afterChange', (event, slick, currentSlide) => {

    /* ==========================================================================
       IGNORE FALSE TOUCH RESTARTS
    ========================================================================== */

    if (previousSlideIndex === currentSlide) {
      return;
    }

    previousSlideIndex = currentSlide;

    isMuted = true;

    updateButtonState();

    /* ==========================================================================
       ONLY RESTART AFTER REAL CHANGE
    ========================================================================== */

    handleActiveSlide(true);

  });

  /* ==========================================================================
     TOUCH FIX
     Prevent accidental drag restart
  ========================================================================== */

  let touchStartX = 0;
  let touchStartY = 0;

  slider.on('touchstart', function(e) {

    const touch = e.originalEvent.touches[0];

    touchStartX = touch.clientX;
    touchStartY = touch.clientY;

  });

  slider.on('touchmove', function(e) {

    const touch = e.originalEvent.touches[0];

    const deltaX = Math.abs(touch.clientX - touchStartX);
    const deltaY = Math.abs(touch.clientY - touchStartY);

    /* ==========================================================================
       SMALL TOUCH = IGNORE
    ========================================================================== */

    if (deltaX < 20 && deltaY < 20) {
      e.stopPropagation();
    }

  });

  /* ==========================================================================
     INIT
  ========================================================================== */

  loadYouTubeAPI().then(() => {

    initYouTubePlayers();

    initShopifyEvents();

    updateButtonState();

    setTimeout(() => {

      handleActiveSlide(false);

    }, 500);

  });

});
